/**
 * Redeeming an invite: the front door that had no door.
 *
 * There is no self-signup, no password-reset mail and no mailer at all, so
 * `POST /auth/invite/accept` is the single way any account after the bootstrap
 * superadmin comes into existence. The admin panel has always handed out a link
 * to `/invite`; until this screen existed that link resolved to the login form
 * and the token was silently dropped, which made the whole invite feature
 * curl-only.
 *
 * The token arrives in the URL **fragment**, never the query string. A query
 * string is written to the reverse proxy's access log, kept in browser history
 * and pasted wherever the link is pasted — and this token is a standing
 * account-creation capability at an admin-chosen role, so a superadmin invite
 * sitting in a logfile is a superadmin bootstrap sitting in a logfile.
 * Fragments are never sent to the server.
 *
 * It is still rendered in an editable field rather than hidden: the panel that
 * issues it also offers the bare token for people who would rather paste one,
 * and a token that cannot be corrected on a typo is a token that needs a whole
 * new invite.
 */
import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { ApiError, api } from '../api/client'
import { MIN_PASSWORD_LENGTH } from '../api/types'
import type { InviteAcceptResponse } from '../api/types'
import {
  AuthCard,
  Field,
  FormError,
  INPUT,
  SignedOutLanguageBar,
  SubmitButton,
} from '../auth/LoginPage'
import { useI18n } from '../i18n'
import type { TFunction } from '../i18n'
import { EnrollmentStep } from './EnrollmentStep'

/**
 * Pull `token` out of a `#token=…` fragment.
 *
 * `URLSearchParams` rather than a split, so a token containing `%2B` or `%3D`
 * — `token_urlsafe` output is base64url today, but a future format need not be
 * — is decoded rather than truncated at the first special character.
 */
export function tokenFromHash(hash: string): string {
  return new URLSearchParams(hash.replace(/^#/, '')).get('token') ?? ''
}

export interface InviteAcceptPageProps {
  /**
   * Enrollment finished: the confirm response set real session cookies, so the
   * app has to re-read `/auth/me` before it navigates anywhere. That is the
   * router's job, not this screen's, so it is handed up. Defaults to a plain
   * navigation for the component's own tests, which mount it without a session.
   */
  onEnrolled?: () => void
  /**
   * The scoped enrollment token expired before the user finished. This screen
   * cannot re-issue one -- and it cannot go back to its own form either, since
   * the invite token was spent creating the account -- so the router sends them
   * to the sign-in form, which answers `enrollment_required` with a fresh token.
   */
  onEnrollmentExpired?: () => void
}

export function InviteAcceptPage({
  onEnrolled,
  onEnrollmentExpired,
}: InviteAcceptPageProps = {}) {
  const { t } = useI18n()
  const { hash } = useLocation()
  const navigate = useNavigate()
  const [token, setToken] = useState(() => tokenFromHash(hash))
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const [created, setCreated] = useState<InviteAcceptResponse | null>(null)

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (pending) return

    if (!token.trim()) {
      setError(t('account.inviteAccept.error.noToken'))
      return
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(t('account.inviteAccept.error.short', { min: MIN_PASSWORD_LENGTH }))
      return
    }
    if (password !== confirm) {
      setError(t('account.inviteAccept.error.mismatch'))
      return
    }

    setPending(true)
    setError(null)
    try {
      // Deliberately does NOT sign the user in: the backend issues no cookies
      // here, only the scoped enrollment token the next step runs on.
      setCreated(
        await api.post<InviteAcceptResponse>('/auth/invite/accept', {
          token: token.trim(),
          password,
        }),
      )
      setPassword('')
      setConfirm('')
    } catch (err) {
      setError(describeInviteError(err, t))
    } finally {
      setPending(false)
    }
  }

  /**
   * The account exists and owes a second factor, which is the whole of what it
   * can do. Straight into enrollment on the same screen: the old "you can sign
   * in now" panel was written for a world where 2FA was optional, and a login
   * form would only hand this user back the token they are already holding.
   */
  if (created?.enrollment_token) {
    return (
      <EnrollmentStep
        enrollmentToken={created.enrollment_token}
        email={created.email}
        onDone={() => (onEnrolled ? onEnrolled() : navigate('/', { replace: true }))}
        onExpired={() =>
          onEnrollmentExpired ? onEnrollmentExpired() : navigate('/login', { replace: true })
        }
      />
    )
  }

  /**
   * A 201 with no token: not something the current server does, and not a
   * reason to invent a way past the gate either. Signing in is the recovery —
   * the login answers `enrollment_required` and mints a fresh token — so this
   * says exactly that rather than claiming the account is ready to use.
   */
  if (created) {
    return (
      <main className="flex h-full flex-col items-center justify-center gap-3 bg-bg px-4">
        <SignedOutLanguageBar />
        <div className="w-full max-w-sm space-y-4 rounded-lg border border-border bg-surface p-6 shadow-sm">
          <header className="space-y-1">
            <h1 className="text-lg font-semibold text-fg">
              {t('account.inviteAccept.created.title')}
            </h1>
            <p className="text-xs text-muted">
              {t('account.inviteAccept.created.body', {
                subject: created.email || t('account.inviteAccept.created.fallbackSubject'),
              })}
            </p>
          </header>
          <p role="status" className="rounded-md bg-healthy/10 px-3 py-2 text-sm text-healthy">
            {t('account.inviteAccept.created.next')}
          </p>
          <Link
            to="/login"
            className="flex w-full items-center justify-center rounded-md bg-accent px-3 py-2
                       text-sm font-medium text-white"
          >
            {t('account.inviteAccept.signIn')}
          </Link>
        </div>
      </main>
    )
  }

  return (
    <AuthCard
      title={t('account.inviteAccept.title')}
      subtitle={t('account.inviteAccept.subtitle')}
      onSubmit={submit}
    >
      <Field
        id="invite-token"
        label={t('account.inviteAccept.tokenLabel')}
        hint={t('account.inviteAccept.tokenHint')}
      >
        <input
          id="invite-token"
          name="token"
          type="text"
          autoComplete="off"
          required
          value={token}
          onChange={(e) => setToken(e.target.value)}
          className={`${INPUT} font-mono`}
        />
      </Field>

      <Field
        id="invite-password"
        label={t('account.inviteAccept.passwordLabel')}
        hint={t('account.inviteAccept.passwordHint', { min: MIN_PASSWORD_LENGTH })}
      >
        <input
          id="invite-password"
          name="password"
          type="password"
          autoComplete="new-password"
          required
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={INPUT}
        />
      </Field>

      <Field id="invite-confirm" label={t('account.inviteAccept.confirmLabel')}>
        <input
          id="invite-confirm"
          name="confirm_password"
          type="password"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className={INPUT}
        />
      </Field>

      <FormError message={error} />

      <SubmitButton pending={pending}>{t('account.inviteAccept.submit')}</SubmitButton>

      {/* The commonest failure on this form is a link that was already
          redeemed, and the person it happens to already has an account. Asking
          their admin for a new invite cannot help them -- that request answers
          409 -- so the way in has to be on the page. */}
      <p className="text-xs text-muted">
        {t('account.inviteAccept.already')}{' '}
        <Link to="/login" className="font-medium text-accent underline">
          {t('account.inviteAccept.signIn')}
        </Link>
      </p>
    </AuthCard>
  )
}

/**
 * The backend answers one message — "invalid or expired invite" — for unknown,
 * expired and already-used tokens alike, because distinguishing them would
 * confirm which tokens exist. Passing it through verbatim is the point: it is
 * the only sentence that tells the recipient to ask for a new invite rather
 * than retype their password.
 */
function describeInviteError(err: unknown, t: TFunction): string {
  if (err instanceof ApiError) {
    if (err.status === 400) {
      // Unknown, expired and ALREADY-USED all arrive as this one message, and
      // the last of those is the one that happens most: a reload of the link
      // after the account was created. "Ask for a new one" is wrong advice
      // there -- the admin can only answer 409 -- so both routes are named.
      return t('account.inviteAccept.error.invalid', {
        detail: err.detail || t('account.inviteAccept.error.invalidFallback'),
      })
    }
    if (err.status === 409) {
      return t('account.inviteAccept.error.exists')
    }
    if (err.status === 422) {
      return t('account.inviteAccept.error.short', { min: MIN_PASSWORD_LENGTH })
    }
    return t('account.inviteAccept.error.server', { status: err.status })
  }
  return t('account.inviteAccept.error.network')
}

export default InviteAcceptPage
